import React, { useEffect, useMemo, useState } from 'react';
import {
  Button,
  Card,
  Form,
  Input,
  InputNumber,
  Popconfirm,
  Select,
  message,
} from 'antd';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import PersianDatePicker from '../PersianDatePicker';
import { formatMoneyFa as formatMoney } from '../../utils/formatPriceToman';
import { show422Error, ValidationError } from '../../utils/api';
import { scrollToFirstError } from '../../utils/formHelpers';

const { Option } = Select;
const { TextArea } = Input;

const statusOptions = [
  { value: 'pending', label: 'در انتظار' },
  { value: 'on-hold', label: 'در حالت انتظار' },
  { value: 'failed', label: 'ناموفق' },
  { value: 'processing', label: 'در حال پردازش' },
  { value: 'completed', label: 'تکمیل‌شده' },
  { value: 'canceled', label: 'لغوشده' },
];

const toJalaliDate = (value) => {
  if (!value) return null;
  if (typeof value === 'string') return value.replace(/^j/i, '').replace(/-/g, '/');
  if (value?.format && typeof value.format === 'function') return value.format('YYYY/MM/DD').replace(/^j/i, '');
  return null;
};

const createEmptyItem = () => ({
  item_id: null,
  product_id: null,
  item_name: '',
  quantity: 1,
  unit_id: null,
  sku: '',
  cost_per_item: 0,
  amount: 0,
  item_summary: '',
});

const OrderForm = ({
  options,
  initialValues,
  initialItems,
  isEdit,
  submitLoading,
  onSubmit,
  lockClient = false,
  lockProject = false,
}) => {
  const [form] = Form.useForm();
  const [items, setItems] = useState(initialItems?.length ? initialItems : [createEmptyItem()]);
  const [products, setProducts] = useState(options?.products || []);
  const selectedCategory = Form.useWatch('category_id', form);
  const selectedStatus = Form.useWatch('status', form);

  useEffect(() => {
    if (initialValues) form.setFieldsValue(initialValues);
  }, [initialValues, form]);

  useEffect(() => {
    if (initialItems?.length) setItems(initialItems);
  }, [initialItems]);

  useEffect(() => {
    setProducts(options?.products || []);
  }, [options?.products]);

  useEffect(() => {
    if (!selectedCategory) {
      setProducts(options?.products || []);
      return;
    }
    const filtered = (options?.products || []).filter((p) => p.category_id === selectedCategory);
    setProducts(filtered);
  }, [selectedCategory, options?.products]);

  const subTotal = useMemo(() => items.reduce((sum, it) => sum + Number(it.amount || 0), 0), [items]);
  const discountValue = Number(Form.useWatch('discount_value', form) || 0);
  const discountType = Form.useWatch('discount_type', form) || 'percent';
  const discountAmount = useMemo(() => {
    if (discountType === 'percent') return (subTotal * discountValue) / 100;
    return Math.min(subTotal, discountValue);
  }, [subTotal, discountType, discountValue]);
  const total = useMemo(() => subTotal - discountAmount, [subTotal, discountAmount]);

  const setItemValue = (index, patch) => {
    setItems((prev) => {
      const next = [...prev];
      const updated = { ...next[index], ...patch };
      updated.amount = Number(updated.quantity || 0) * Number(updated.cost_per_item || 0);
      next[index] = updated;
      return next;
    });
  };

  const addItem = () => setItems((prev) => [...prev, createEmptyItem()]);
  const removeItem = (index) => setItems((prev) => prev.filter((_, i) => i !== index));

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      const validItems = items.filter((it) => it.product_id && it.item_name && Number(it.quantity) > 0);
      if (!validItems.length) {
        message.warning('حداقل یک آیتم معتبر وارد کنید');
        return;
      }

      if (values.status === 'completed' && !values.bank_account_id) {
        form.setFields([{ name: 'bank_account_id', errors: ['برای وضعیت تکمیل‌شده، حساب بانکی الزامی است'] }]);
        form.scrollToField('bank_account_id', { behavior: 'smooth', block: 'center' });
        return;
      }

      const payload = {
        order_number: values.order_number,
        client_id: values.client_id,
        shipping_address: values.shipping_address || null,
        project_id: values.project_id || null,
        company_address_id: values.company_address_id || null,
        status: values.status,
        bank_account_id: values.status === 'completed' ? (values.bank_account_id || null) : null,
        category_id: values.category_id || null,
        sort_order: validItems.map((it) => it.product_id),
        item_ids: validItems.map((it) => it.product_id),
        item_name: validItems.map((it) => it.item_name),
        quantity: validItems.map((it) => Number(it.quantity || 0)),
        product_id: validItems.map((it) => it.product_id),
        unit_id: validItems.map((it) => it.unit_id || null),
        sku: validItems.map((it) => it.sku || ''),
        cost_per_item: validItems.map((it) => Number(it.cost_per_item || 0)),
        amount: validItems.map((it) => Number(it.amount || 0)),
        item_summary: validItems.map((it) => it.item_summary || ''),
        sub_total: Number(subTotal.toFixed(2)),
        discount_value: Number(values.discount_value || 0),
        discount_type: values.discount_type || 'percent',
        total: Number(total.toFixed(2)),
        note: values.note || '',
      };

      if (isEdit) {
        payload.order_date = toJalaliDate(values.order_date);
        payload.order_item_ids = validItems.map((it) => it.item_id).filter(Boolean);
      }

      await onSubmit(payload);
    } catch (error) {
      if (error?.errorFields?.length) {
        scrollToFirstError(form);
        return;
      }
      if (error instanceof ValidationError || error?.error?.details) {
        show422Error(error, form);
      } else {
        message.error(error?.message || 'خطا در ثبت سفارش');
      }
    }
  };

  const clients = options?.clients || [];
  const projects = options?.projects || [];
  const companyAddresses = options?.company_addresses || [];
  const categories = options?.categories || [];
  const units = options?.units || [];
  const bankDetails = options?.bank_details || [];

  return (
    <Form
      form={form}
      layout="vertical"
      onFinish={handleSubmit}
      scrollToFirstError={{ behavior: 'smooth', block: 'center' }}
      style={{ fontFamily: 'CustomFont' }}
    >
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <Form.Item name="order_number" label="شماره سفارش" rules={[{ required: true, message: 'الزامی است' }]}>
          <InputNumber min={1} className="w-full" />
        </Form.Item>
        {isEdit && (
          <Form.Item name="order_date" label="تاریخ سفارش">
            <PersianDatePicker />
          </Form.Item>
        )}
        <Form.Item name="client_id" label="مشتری" rules={[{ required: true, message: 'الزامی است' }]}>
          <Select showSearch optionFilterProp="children" disabled={lockClient}>
            {clients.map((c) => <Option key={c.id} value={c.id}>{c.name_salutation || c.name}</Option>)}
          </Select>
        </Form.Item>
        <Form.Item name="project_id" label="پروژه">
          <Select allowClear={!lockProject} disabled={lockProject}>
            {projects.map((p) => <Option key={p.id} value={p.id}>{p.project_name}</Option>)}
          </Select>
        </Form.Item>
        <Form.Item name="company_address_id" label="آدرس شرکت">
          <Select allowClear>
            {companyAddresses.map((a) => <Option key={a.id} value={a.id}>{a.location}</Option>)}
          </Select>
        </Form.Item>
        <Form.Item name="status" label="وضعیت" rules={[{ required: true, message: 'الزامی است' }]}>
          <Select>
            {statusOptions.map((s) => <Option key={s.value} value={s.value}>{s.label}</Option>)}
          </Select>
        </Form.Item>
        <Form.Item
          name="bank_account_id"
          label="حساب بانکی (برای تکمیل‌شده)"
          rules={[
            ({ getFieldValue }) => ({
              validator(_, value) {
                if (getFieldValue('status') !== 'completed' || value) return Promise.resolve();
                return Promise.reject(new Error('برای وضعیت تکمیل‌شده حساب بانکی الزامی است'));
              },
            }),
          ]}
        >
          <Select allowClear disabled={selectedStatus !== 'completed'}>
            {bankDetails.map((b) => <Option key={b.id} value={b.id}>{b.account_name} {b.bank_name ? `(${b.bank_name})` : ''}</Option>)}
          </Select>
        </Form.Item>
        <Form.Item name="category_id" label="دسته‌بندی">
          <Select allowClear>
            {categories.map((c) => <Option key={c.id} value={c.id}>{c.category_name}</Option>)}
          </Select>
        </Form.Item>
        <Form.Item name="discount_value" label="مقدار تخفیف">
          <InputNumber min={0} className="w-full" />
        </Form.Item>
        <Form.Item name="discount_type" label="نوع تخفیف">
          <Select>
            <Option value="percent">درصدی</Option>
            <Option value="fixed">ثابت</Option>
          </Select>
        </Form.Item>
      </div>

      <Form.Item name="shipping_address" label="آدرس ارسال">
        <Input />
      </Form.Item>

      <Card size="small" title="آیتم‌ها" className="mb-3">
        {items.map((it, index) => (
          <div key={index} className="grid grid-cols-1 md:grid-cols-12 gap-2 mb-2 p-2 border rounded dark:border-gray-600">
            <div className="md:col-span-3">
              <Select
                className="w-full"
                placeholder="محصول"
                value={it.product_id}
                onChange={(val) => {
                  const p = products.find((x) => x.id === val);
                  setItemValue(index, {
                    product_id: val,
                    item_name: p?.name || it.item_name,
                    sku: p?.sku || it.sku,
                    cost_per_item: Number(p?.price || 0),
                    unit_id: p?.unit_id || it.unit_id,
                  });
                }}
                showSearch
                optionFilterProp="children"
              >
                {products.map((p) => <Option key={p.id} value={p.id}>{p.name}</Option>)}
              </Select>
            </div>
            <div className="md:col-span-2">
              <Input placeholder="نام آیتم" value={it.item_name} onChange={(e) => setItemValue(index, { item_name: e.target.value })} />
            </div>
            <div className="md:col-span-1">
              <InputNumber min={1} className="w-full" value={it.quantity} onChange={(v) => setItemValue(index, { quantity: Number(v || 0) })} />
            </div>
            <div className="md:col-span-1">
              <Select className="w-full" placeholder="واحد" value={it.unit_id} onChange={(v) => setItemValue(index, { unit_id: v })}>
                {units.map((u) => <Option key={u.id} value={u.id}>{u.unit_type}</Option>)}
              </Select>
            </div>
            <div className="md:col-span-1">
              <Input placeholder="SKU" value={it.sku} onChange={(e) => setItemValue(index, { sku: e.target.value })} />
            </div>
            <div className="md:col-span-2">
              <InputNumber min={0} className="w-full" value={it.cost_per_item} onChange={(v) => setItemValue(index, { cost_per_item: Number(v || 0) })} />
            </div>
            <div className="md:col-span-1">
              <Popconfirm title="حذف آیتم؟" onConfirm={() => removeItem(index)} okText="بله" cancelText="خیر">
                <Button danger icon={<DeleteOutlined />} disabled={items.length === 1} />
              </Popconfirm>
            </div>
            <div className="md:col-span-12">
              <Input placeholder="خلاصه آیتم" value={it.item_summary} onChange={(e) => setItemValue(index, { item_summary: e.target.value })} />
            </div>
          </div>
        ))}
        <Button icon={<PlusOutlined />} onClick={addItem}>افزودن آیتم</Button>
      </Card>

      <Card size="small" className="mb-3">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-sm">
          <div>جمع کل: <b>{formatMoney(subTotal)}</b></div>
          <div>تخفیف: <b>{formatMoney(discountAmount)}</b></div>
          <div>مبلغ نهایی: <b>{formatMoney(total)}</b></div>
        </div>
      </Card>

      <Form.Item name="note" label="یادداشت">
        <TextArea rows={2} />
      </Form.Item>

      <div className="flex justify-end gap-2">
        <Button onClick={() => window.history.back()}>انصراف</Button>
        <Button type="primary" htmlType="submit" className="bg-green-600 hover:bg-green-700 border-0" loading={submitLoading}>
          {isEdit ? 'ذخیره تغییرات' : 'ایجاد سفارش'}
        </Button>
      </div>
    </Form>
  );
};

export default OrderForm;
